import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Users,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "All Employees", path: "/employees", desc: "Search, filter, and manage employee records" },
  { label: "Add Employee", path: "/employees?module=employees&view=add", desc: "Create a new employee via the 5-step wizard" },
  { label: "Bulk Import", path: "/employees?module=employees&view=import", desc: "Upload employees via CSV or Excel" },
  { label: "Reporting Hierarchy", path: "/employees?module=hierarchy", desc: "View and edit who reports to whom" },
  { label: "Employee Lifecycle", path: "/employees?module=lifecycle", desc: "Onboarding, offboarding, and exit analytics" },
  { label: "Employee Setup", path: "/settings/employee-configuration", desc: "Configure employee code prefix and settings" },
  { label: "Employee Reports", path: "/reports/employees", desc: "Generate employee summary and detail reports" },
];

const EMPLOYEE_TROUBLESHOOTING = [
  {
    problem: "Cannot find an employee in the employee list",
    check: "The employee list defaults to showing only active employees. If the employee has been set to 'Inactive' or 'Terminated', they will not appear unless you toggle the 'Include Inactive' filter. Also check your search term — the list searches by name, employee code, email, department, and designation.",
    action: "Go to All Employees, enable 'Include Inactive' in the filter options, and search again. If the employee still does not appear, they may not have been created yet.",
    path: "/employees",
    pathLabel: "All Employees",
  },
  {
    problem: "Employee information appears incorrect or outdated",
    check: "Employee records are not updated automatically. If personal details, department, designation, or compensation have changed, the record must be manually updated by an admin or HR user.",
    action: "From the employee list, click on the employee row to open the preview drawer, then click 'Open full record' or click the Edit button in the Actions column. Update the relevant fields in the 5-step wizard and save.",
    path: "/employees",
    pathLabel: "All Employees",
  },
  {
    problem: "Cannot add a new employee — 'Account is not linked to an organisation'",
    check: "This error means your user account is not associated with any organisation in BrixiGo. Employee creation requires an active organisation context.",
    action: "Complete the Organisation Setup in Settings before adding employees. If you believe your account should be linked, contact your admin to verify your organisation assignment.",
    path: "/employees?module=employees&view=add",
    pathLabel: "Add Employee",
  },
  {
    problem: "Employee creation fails — required fields are missing",
    check: "The Add Employee wizard requires specific fields at each step. Step 1 requires: Employee Type, First Name, Work Email, Gender, and Date of Birth. Step 2 requires: System Access Role. Step 3 requires: Date of Joining and Annual CTC. Steps 4 (Banking) and 5 (Statutory) are optional.",
    action: "Review each step of the wizard for highlighted validation errors. Fill in all required fields before proceeding. Required fields are marked and the form will not submit until they are completed.",
    path: "/employees?module=employees&view=add",
    pathLabel: "Add Employee",
  },
  {
    problem: "Banking details validation error — IFSC format or account number mismatch",
    check: "In Step 4 (Banking), the IFSC code must be exactly 11 characters in the format 4 letters + 0 + 6 alphanumeric (e.g. HDFC0001234). The 'Account Number' and 'Confirm Account Number' fields must match exactly.",
    action: "Re-enter the IFSC code in the correct format. For account numbers, carefully re-enter both fields ensuring they are identical. The account number should be 9–18 digits.",
    path: "/employees?module=employees&view=add",
    pathLabel: "Add Employee",
  },
  {
    problem: "Statutory details validation error — UAN, PAN, or Aadhaar format rejected",
    check: "In Step 5 (Statutory), UAN must be exactly 12 digits, PAN must match the format AAAAA9999A (5 letters, 4 digits, 1 letter), and Aadhaar must be exactly 12 digits. These fields are optional but are validated if entered.",
    action: "Correct the format of the rejected field. UAN: 12 numeric digits. PAN: e.g. ABCDE1234F. Aadhaar: 12 numeric digits. Leave the field blank if you don't have the information yet — it can be added later.",
    path: "/employees?module=employees&view=add",
    pathLabel: "Add Employee",
  },
  {
    problem: "Bulk import file rejected or showing validation errors",
    check: "Bulk import accepts only .csv and .xlsx files. Required columns are: employee_type, first_name, work_email, gender, date_of_birth, role, date_of_joining, annual_ctc. The employee_code column is not needed — codes are auto-generated. Dates must be in YYYY-MM-DD format.",
    action: "Download the import template from the Bulk Import page to get the correct format. Fill in all required columns, ensure date formats are correct, and re-upload. The system will show validation errors per row before importing.",
    path: "/employees?module=employees&view=import",
    pathLabel: "Bulk Import",
  },
  {
    problem: "Employee not appearing in payroll or salary structure assignment",
    check: "For an employee to appear in payroll, they must have status 'Active', a valid Date of Joining within the payroll period, and an assigned salary structure. If any of these are missing, the employee will not be included in payroll processing.",
    action: "Verify the employee's status is 'Active' and their Date of Joining is set. Then go to Employee Salary Structure to assign a pay template. The employee's Annual CTC is used as the base for the salary structure breakdown.",
    path: "/employeesalarystructure",
    pathLabel: "Salary Structure",
  },
  {
    problem: "Employee cannot access the system or see their own data",
    check: "System access is controlled by the 'System Access Role' field set in Step 2 of the employee wizard. Available roles are: employee, manager, hr, admin. An employee with the 'employee' role can access self-service features. If the role is not set or login credentials are not shared, the employee cannot access the system.",
    action: "Go to the employee record, open the edit wizard, and verify the System Access Role is set to the appropriate level. Ensure the employee's Work Email is correct, as it is used for login.",
    path: "/employees",
    pathLabel: "All Employees",
  },
  {
    problem: "Department, designation, or location dropdown is empty when creating an employee",
    check: "These dropdowns are populated from your organisation's configured departments, designations, and locations. If none have been created yet, the dropdowns will be empty.",
    action: "In the Add Employee wizard (Step 2), use the '+' button next to each dropdown to create a new department, designation, or location inline. Alternatively, configure them in advance from Settings.",
    path: "/employees?module=employees&view=add",
    pathLabel: "Add Employee",
  },
  {
    problem: "Reporting manager not showing for an employee",
    check: "The reporting manager assignment is separate from the employee creation wizard. It is managed through the Reporting Hierarchy module within the People Hub.",
    action: "Go to Reporting Hierarchy and assign the correct reporting manager to the employee. Use the search to find the employee, then edit their reporting line.",
    path: "/employees?module=hierarchy",
    pathLabel: "Reporting Hierarchy",
  },
];

export default function EmployeeSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return EMPLOYEE_TROUBLESHOOTING;
    return EMPLOYEE_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-amber">
              <Users size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Employee Data</h1>
              <p className="hs-hero-sub">
                Troubleshoot employee record and profile issues.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search employee issues — e.g. missing, import, salary structure..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Employee Data Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Employee Data Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No employee issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed employee information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed employee information?</h2>
              <p className="sc-info-row-desc">
                Visit the Employee Help Guide for detailed documentation, FAQs and step-by-step employee management instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/employees")}
            >
              Open Employee Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
